<template>
  <!-- One message: this side's on the right, the other side's on the left.
       A run of messages sits under one line — the other side's avatar, who
       wrote it and when ("You" for the viewer's own) — and the last of a run
       from this side says under it whether the other side has read it.
       Text (its links clickable, a few emoji on their own shown large), a
       voice message, pictures and files, or that it was deleted — and, for a
       reply, the message it replies to, quoted on top (click it to go
       there). Under it, its emoji reactions.

       Anyone can reply to any message and react to it: from its menu (the ⋯
       beside it on hover, a right-click, or a long press on a phone), with
       the smiley and the reply arrow beside it, or — on a phone — by swiping
       it to the right to reply. The author can also edit or delete it. In a
       resolved conversation a message can only be copied, or deleted by its
       author. A message still on its way says so, and one that failed can be
       sent again. -->
  <div
    class="sc-msg"
    :class="{
      'sc-msg--mine': mine,
      'sc-msg--theirs': !mine,
      'sc-msg--first': first,
      'sc-msg--last': last,
      'is-local': message.local,
      'is-failed': message.status === 'failed',
      'is-menu-open': menuOpen,
      'is-swiping': swiping,
      'is-highlighted': highlighted,
      'has-reactions': reactions.length > 0,
    }"
    :data-message-id="message.local ? null : message.id"
  >
    <div class="sc-msg__main">
      <div v-if="first" class="sc-msg__head">
        <chat-avatar v-if="!mine && avatar" :name="avatar.name" :image="avatar.image || ''" :square="Boolean(avatar.square)" :size="22" />
        <span class="sc-msg__head-name">{{ headName }}</span>
        <span class="sc-msg__head-time" :title="fullTime">{{ clock }}</span>
      </div>
      <span
        v-if="canReply"
        class="sc-msg__swipe"
        :style="{ opacity: Math.min(swipeX / SWIPE_TRIGGER_PX, 1), transform: `scale(${swipeX >= SWIPE_TRIGGER_PX ? 1.15 : 0.6 + 0.4 * Math.min(swipeX / SWIPE_TRIGGER_PX, 1)})` }"
        aria-hidden="true"
      >
        <i class="pi pi-reply"></i>
      </span>
      <div ref="line" class="sc-msg__line" :style="swipeX ? { transform: `translateX(${swipeX}px)` } : null">
        <div
          class="sc-msg__stack"
          :title="fullTime"
          @contextmenu="onContextMenu"
          @click.capture="onClickCapture"
          @touchstart.passive="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd"
          @touchcancel="onTouchEnd"
        >
          <div v-if="message.isDeleted" class="sc-bubble sc-bubble--deleted">
            <i class="pi pi-ban" aria-hidden="true"></i>
            <span>{{ isMe ? 'You deleted this message' : 'This message was deleted' }}</span>
          </div>
          <template v-else>
            <!-- A reply that sends files or a voice message: the quote on top
                 of them. A reply in text has it inside its bubble instead. -->
            <chat-quote
              v-if="quote && !quoteInBubble"
              class="sc-quote--standalone"
              :quote="quote"
              :author-label="quoteLabel"
              :mine="mine"
              @jump="$emit('jump')"
            />
            <div v-if="voice" class="sc-bubble sc-bubble--voice">
              <chat-voice-player :src="voice.url" :duration="Number(voice.duration) || 0" :waveform="voice.waveform || []" :mine="mine" />
            </div>
            <chat-attachments
              v-if="files.length"
              :attachments="files"
              :mine="mine"
              :local="Boolean(message.local)"
              @open-image="$emit('open-image', $event)"
            />
            <div
              v-if="message.body"
              class="sc-bubble"
              :class="[emojiCount ? ['sc-bubble--emoji', `sc-bubble--emoji-${emojiCount}`] : null, { 'sc-bubble--quoting': quoteInBubble }]"
            >
              <chat-quote v-if="quoteInBubble" :quote="quote" :author-label="quoteLabel" :mine="mine" @jump="$emit('jump')" />
              <span class="sc-bubble__text"><template v-for="(part, index) in parts" :key="index"><a
                v-if="part.href"
                class="sc-link"
                :href="part.href"
                target="_blank"
                rel="noopener noreferrer nofollow"
              >{{ part.text }}</a><template v-else>{{ part.text }}</template></template></span>
              <span v-if="message.editedAt" class="sc-bubble__edited">edited</span>
            </div>
          </template>
        </div>

        <div v-if="actions.length" ref="actions" class="sc-msg__actions">
          <button
            v-if="canReact"
            type="button"
            class="sc-msg__action sc-msg__react"
            aria-label="React"
            title="React"
            :aria-expanded="menuOpen && menuMode === 'react' ? 'true' : 'false'"
            @click="toggleMenu('react')"
          >
            <i class="pi pi-face-smile" aria-hidden="true"></i>
          </button>
          <button
            v-if="canReply"
            type="button"
            class="sc-msg__action sc-msg__quick-reply"
            aria-label="Reply"
            title="Reply"
            @click="$emit('reply')"
          >
            <i class="pi pi-reply" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="sc-msg__action sc-msg__more"
            :aria-expanded="menuOpen && menuMode === 'full' ? 'true' : 'false'"
            aria-haspopup="menu"
            aria-label="Message options"
            title="More"
            @click="toggleMenu('full')"
          >
            <i class="pi pi-ellipsis-h" aria-hidden="true"></i>
          </button>
          <div
            v-if="menuOpen"
            ref="menu"
            class="sc-menu"
            :class="{ 'sc-menu--below': placement === 'below', 'sc-menu--reactions': menuMode === 'react' }"
            @keydown.esc.stop.prevent="closeMenu"
          >
            <div v-if="canReact && !confirmingDelete" class="sc-react-bar" role="group" aria-label="React with">
              <button
                v-for="emoji in quickReactions"
                :key="emoji"
                type="button"
                class="sc-react-bar__item"
                :class="{ 'is-mine': emoji === myReaction }"
                :title="emoji === myReaction ? 'Take your reaction back' : `React with ${emoji}`"
                :aria-label="emoji === myReaction ? `Take back ${emoji}` : `React with ${emoji}`"
                @click="react(emoji)"
              >{{ emoji }}</button>
              <button
                type="button"
                class="sc-react-bar__item sc-react-bar__more"
                :class="{ 'is-mine': myReaction && !quickReactions.includes(myReaction) }"
                title="More emoji"
                aria-label="React with another emoji"
                @click="moreReactions"
              >
                <span v-if="myReaction && !quickReactions.includes(myReaction)" aria-hidden="true">{{ myReaction }}</span>
                <i v-else class="pi pi-plus" aria-hidden="true"></i>
              </button>
            </div>
            <ul v-if="menuMode === 'full'" class="sc-menu__list" role="menu">
              <template v-if="confirmingDelete">
                <li class="sc-menu__note">Delete this message for everyone?</li>
                <li>
                  <button type="button" class="sc-menu__item sc-menu__item--danger" role="menuitem" @click="confirmDelete">
                    <i class="pi pi-trash" aria-hidden="true"></i> Delete
                  </button>
                </li>
                <li>
                  <button type="button" class="sc-menu__item" role="menuitem" @click="confirmingDelete = false">
                    <i class="pi pi-arrow-left" aria-hidden="true"></i> Keep it
                  </button>
                </li>
              </template>
              <template v-else>
                <li v-for="action in actions" :key="action.key">
                  <button
                    type="button"
                    class="sc-menu__item"
                    :class="{ 'sc-menu__item--danger': action.key === 'delete' }"
                    role="menuitem"
                    @click="run(action.key)"
                  >
                    <i class="pi" :class="action.icon" aria-hidden="true"></i> {{ action.label }}
                  </button>
                </li>
              </template>
            </ul>
          </div>
        </div>
      </div>

      <div v-if="reactions.length" class="sc-reactions" :class="{ 'sc-reactions--mine': mine }">
        <button
          v-for="group in reactions"
          :key="group.emoji"
          type="button"
          class="sc-reaction"
          :class="{ 'is-mine': group.reacted }"
          :disabled="!canReact"
          :title="reactionLabel(group)"
          :aria-label="reactionLabel(group)"
          :aria-pressed="group.reacted ? 'true' : 'false'"
          @click="react(group.emoji)"
        >
          <span class="sc-reaction__emoji" aria-hidden="true">{{ group.emoji }}</span>
          <span v-if="group.count > 1" class="sc-reaction__count" aria-hidden="true">{{ group.count }}</span>
        </button>
      </div>

      <div v-if="message.local" class="sc-msg__status" role="status">
        <template v-if="message.status === 'failed'">
          <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
          <span>Not sent.</span>
          <button type="button" class="sc-msg__status-btn" @click="$emit('retry')">Try again</button>
          <span aria-hidden="true">·</span>
          <button type="button" class="sc-msg__status-btn" @click="$emit('discard')">Remove</button>
        </template>
        <template v-else>
          <span class="sc-spinner" aria-hidden="true"></span>
          <span>{{ uploadLabel }}</span>
        </template>
      </div>
      <div v-else-if="last && mine && !message.isDeleted" class="sc-msg__receipt">
        {{ seen ? 'Read' : 'Sent' }}
      </div>
    </div>
  </div>
</template>
<script>
import chatAvatar from '@/components/chat/Chat_Avatar.vue';
import chatAttachments from '@/components/chat/Chat_Attachments.vue';
import chatQuote from '@/components/chat/Chat_Quote.vue';
import chatVoicePlayer from '@/components/chat/Chat_Voice_Player.vue';
import {
  chatClock,
  chatEmojiOnlyCount,
  chatFullTime,
  chatReactionLabel,
  chatTextParts,
  CHAT_QUICK_REACTIONS,
} from '@/utils/chat';

// A swipe to the right this far replies (WhatsApp's gesture); it goes no further.
const SWIPE_TRIGGER_PX = 56;
const SWIPE_MAX_PX = 84;
const LONG_PRESS_MS = 480;

export default {
  name: 'ChatMessage',
  components: {
    chatAvatar,
    chatAttachments,
    chatQuote,
    chatVoicePlayer,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    // On the viewer's side of the conversation (right-hand).
    mine: {
      type: Boolean,
      default: false,
    },
    // Written by the viewer themselves, on this side: theirs to edit and delete.
    isMe: {
      type: Boolean,
      default: false,
    },
    // The first / last of a run of messages from the same person.
    first: {
      type: Boolean,
      default: true,
    },
    last: {
      type: Boolean,
      default: true,
    },
    authorLabel: {
      type: String,
      default: '',
    },
    // { name, image, square } — on the line above the other side's runs.
    avatar: {
      type: Object,
      default: null,
    },
    // The other side has read it.
    seen: {
      type: Boolean,
      default: false,
    },
    // The message this one replies to (utils/chat chatQuoteOf), and who wrote it as the viewer knows them.
    quote: {
      type: Object,
      default: null,
    },
    quoteLabel: {
      type: String,
      default: '',
    },
    // Just jumped to from a quote: it flashes.
    highlighted: {
      type: Boolean,
      default: false,
    },
    // Its conversation is resolved: nothing new happens in it.
    closed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['reply', 'react', 'more-reactions', 'jump', 'edit', 'delete', 'copy', 'retry', 'discard', 'open-image'],
  data() {
    return {
      menuOpen: false,
      // 'react': just the reactions (the smiley); 'full': reactions and actions (the ⋯).
      menuMode: 'full',
      placement: 'above',
      confirmingDelete: false,
      swipeX: 0,
      swiping: false,
      quickReactions: CHAT_QUICK_REACTIONS,
      SWIPE_TRIGGER_PX,
    };
  },
  computed: {
    // Who wrote the run, on the line above it.
    headName() {
      if (this.isMe) return 'You';
      return this.authorLabel || (this.mine ? 'You' : '');
    },
    attachments() {
      return Array.isArray(this.message.attachments) ? this.message.attachments : [];
    },
    voice() {
      return this.attachments.find((file) => file.kind === 'voice') || null;
    },
    files() {
      return this.attachments.filter((file) => file.kind !== 'voice');
    },
    parts() {
      return chatTextParts(this.message.body);
    },
    reactions() {
      return this.message.isDeleted || !Array.isArray(this.message.reactions) ? [] : this.message.reactions;
    },
    // The emoji the viewer reacted with, if any.
    myReaction() {
      const mine = this.reactions.find((group) => group.reacted);
      return mine ? mine.emoji : null;
    },
    // A reply in text alone carries its quote inside its bubble.
    quoteInBubble() {
      return Boolean(this.quote && this.message.body && !this.files.length && !this.voice);
    },
    emojiCount() {
      const count = chatEmojiOnlyCount(this.message.body);
      return count > 0 && count <= 3 && !this.files.length && !this.voice && !this.quote ? count : 0;
    },
    clock() {
      return chatClock(this.message.createdAt);
    },
    fullTime() {
      return chatFullTime(this.message.createdAt);
    },
    uploadLabel() {
      const progress = Number(this.message.progress) || 0;
      if (this.attachments.length && progress > 0 && progress < 1) return `Uploading… ${Math.round(progress * 100)}%`;
      return 'Sending…';
    },
    // Anything that has arrived and is still there.
    isThere() {
      return !this.message.local && !this.message.isDeleted;
    },
    // Replied and reacted to while its conversation is open.
    canReply() {
      return this.isThere && !this.closed;
    },
    canReact() {
      return this.isThere && !this.closed;
    },
    actions() {
      if (!this.isThere) return [];
      const list = [];
      if (this.canReply) list.push({ key: 'reply', label: 'Reply', icon: 'pi-reply' });
      if (this.message.body) list.push({ key: 'copy', label: 'Copy text', icon: 'pi-copy' });
      if (this.isMe && !this.voice && !this.closed) list.push({ key: 'edit', label: 'Edit', icon: 'pi-pencil' });
      if (this.isMe) list.push({ key: 'delete', label: 'Delete', icon: 'pi-trash' });
      return list;
    },
  },
  watch: {
    // Resolved while its menu was open: the menu loses what no longer applies.
    closed(isClosed) {
      if (isClosed && this.menuOpen && this.menuMode === 'react') this.closeMenu();
    },
  },
  methods: {
    reactionLabel(group) {
      return chatReactionLabel(group);
    },
    toggleMenu(mode) {
      if (this.menuOpen && this.menuMode === mode) this.closeMenu();
      else this.openMenu(mode);
    },
    // The menu opens above the message, or below it when there is no room
    // above in the conversation.
    openMenu(mode = 'full') {
      if (mode === 'react' && !this.canReact) return;
      this.menuMode = mode;
      this.placement = 'above';
      this.confirmingDelete = false;
      if (!this.menuOpen) {
        this.menuOpen = true;
        document.addEventListener('pointerdown', this.onPointerDown, true);
        document.addEventListener('keydown', this.onKeydown, true);
      }
      this.$nextTick(() => this.place());
    },
    place() {
      const menu = this.$refs.menu;
      const scroller = this.$el && this.$el.closest ? this.$el.closest('.sc-thread__scroll') : null;
      if (!menu || !scroller) return;
      const top = scroller.getBoundingClientRect().top;
      if (menu.getBoundingClientRect().top < top + 4) this.placement = 'below';
    },
    closeMenu() {
      this.menuOpen = false;
      this.confirmingDelete = false;
      document.removeEventListener('pointerdown', this.onPointerDown, true);
      document.removeEventListener('keydown', this.onKeydown, true);
    },
    onPointerDown(event) {
      if (this.$refs.actions && !this.$refs.actions.contains(event.target)) this.closeMenu();
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.closeMenu();
    },
    react(emoji) {
      if (!this.canReact) return;
      this.closeMenu();
      this.$emit('react', emoji);
    },
    // Any other emoji: the conversation opens its picker by the message.
    moreReactions() {
      const rect = this.$refs.line ? this.$refs.line.getBoundingClientRect() : null;
      this.closeMenu();
      this.$emit('more-reactions', rect);
    },
    // A right-click opens the message's own menu — except on a link, a
    // picture or a player, whose browser menu is the useful one.
    onContextMenu(event) {
      if (!this.actions.length || event.target.closest('a, img, audio, button')) return;
      event.preventDefault();
      this.openMenu('full');
    },

    /* --------------------------- touch screens --------------------------- */
    // A long press anywhere on the message opens the menu (reactions on top)
    // — its quote and its player too, but not a link or a picture, whose own
    // long-press menu (open, save) is the useful one. A swipe to the right
    // replies. Whichever the finger does first wins: moving cancels the long
    // press, and only a mostly sideways move is a swipe (anything else
    // scrolls).
    onTouchStart(event) {
      const touch = event.touches && event.touches[0];
      if (!touch || event.touches.length > 1) return;
      this.touch = { x: touch.clientX, y: touch.clientY };
      this.swiping = false;
      this.swipeX = 0;
      this.armed = false;
      this.cancelPress();
      if (!this.actions.length || event.target.closest('a, img')) return;
      this.pressTimer = window.setTimeout(() => {
        this.pressTimer = null;
        this.touch = null;
        this.longPressed = true;
        this.openMenu('full');
        if (navigator.vibrate) navigator.vibrate(12);
      }, LONG_PRESS_MS);
    },
    // A long press is not a tap as well: lifting the finger after one never
    // plays a voice message or follows a quote under it.
    onClickCapture(event) {
      if (Date.now() >= this.suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    },
    onTouchMove(event) {
      const touch = event.touches && event.touches[0];
      if (!this.touch || !touch) return;
      const dx = touch.clientX - this.touch.x;
      const dy = touch.clientY - this.touch.y;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) this.cancelPress();
      if (!this.canReply) return;
      if (!this.swiping) {
        if (dx > 12 && Math.abs(dx) > Math.abs(dy) * 1.5) this.swiping = true;
        else if (Math.abs(dy) > 12) {
          this.touch = null;
          return;
        } else return;
      }
      // Sideways now: the conversation does not scroll under the finger.
      if (event.cancelable) event.preventDefault();
      this.swipeX = Math.max(0, Math.min(dx, SWIPE_MAX_PX));
      if (this.swipeX >= SWIPE_TRIGGER_PX && !this.armed) {
        this.armed = true;
        if (navigator.vibrate) navigator.vibrate(8);
      } else if (this.swipeX < SWIPE_TRIGGER_PX) {
        this.armed = false;
      }
    },
    onTouchEnd(event) {
      this.cancelPress();
      if (this.longPressed) {
        this.longPressed = false;
        this.suppressClickUntil = Date.now() + 700;
        if (event && event.cancelable) event.preventDefault();
      }
      const reply = this.swiping && this.swipeX >= SWIPE_TRIGGER_PX;
      this.swiping = false;
      this.swipeX = 0;
      this.armed = false;
      this.touch = null;
      if (reply) this.$emit('reply');
    },
    cancelPress() {
      if (this.pressTimer) window.clearTimeout(this.pressTimer);
      this.pressTimer = null;
    },
    run(key) {
      if (key === 'delete') {
        this.confirmingDelete = true;
        this.$nextTick(() => this.place());
        return;
      }
      this.closeMenu();
      this.$emit(key);
    },
    confirmDelete() {
      this.closeMenu();
      this.$emit('delete');
    },
  },
  created() {
    this.pressTimer = null;
    this.touch = null;
    this.armed = false;
    this.longPressed = false;
    this.suppressClickUntil = 0;
  },
  beforeUnmount() {
    this.cancelPress();
    this.closeMenu();
  },
};
</script>
